import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LicencePurchasesPanel } from '../components/LicencePurchasesPanel';
import type { FormEvent } from 'react';
import { KeyValue, KeyValueList, PageHeader, Panel } from '../components/Page';
import { EmptyState, ErrorState, InlineError, SkeletonText, TableSkeleton } from '../components/Feedback';
import { Chip, ConnectionStatusChip } from '../components/Chips';
import { SelectField, TextField, ToggleField, enumOptions, parseEnumValue } from '../components/Form';
import { PasswordField, PasswordStrength } from '../components/Password';
import { TableWrap } from '../components/Table';
import { ConfirmDialog, Drawer } from '../components/Overlay';
import { PlanUsageCard } from '../components/Onboarding';
import { tenantFeatureState, useOnboarding } from '../lib/onboarding';
import { RequireRole, useAuth } from '../lib/auth';
import { account, companyAccess, invitations, licensing, qbo, users } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { assessPassword, usePasswordPolicy } from '../lib/password';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { GatewayRole, GatewayRoleDescriptions, GatewayRoleLabels, QboConnectionStatus } from '../lib/enums';
import { formatDate, formatDateTime, formatMoney, formatRelative, parseDecimalInput } from '../lib/format';
import type { InvitationDto, LicencePackageOfferDto, UserDto } from '../lib/types';

// ---------------------------------------------------------------------------
// Plans and licences
// ---------------------------------------------------------------------------

const BILLING_PERIOD_OPTIONS = [
  { value: 'MONTH', label: 'Monthly' },
  { value: 'YEAR', label: 'Annual' },
];

const AI_POWER_SORT_ORDER: Record<string, number> = {
  ESSENTIAL: 10,
  PROFESSIONAL: 20,
  ADVANCED: 30,
  MAXIMUM: 40,
};

type BillingPeriod = 'MONTH' | 'YEAR';

type PlanSelection = {
  billingPeriod: BillingPeriod;
  aiPowerCode: string;
};

function normalizeBillingPeriod(value: string | null | undefined): BillingPeriod {
  return (value ?? '').toUpperCase() === 'YEAR' ? 'YEAR' : 'MONTH';
}

function billingPeriodLabel(value: string | null | undefined): string {
  return normalizeBillingPeriod(value) === 'YEAR' ? 'Annual' : 'Monthly';
}

/**
 * THE PROCESSING-OPTION NAME A USER SEES IS DECIDED HERE, NOT UPSTREAM.
 *
 * The catalogue carries a free-text description for each processing option, and that text is
 * written for other audiences - it can name internal engine tiers and model providers that a
 * QuickBooks user must never be shown. The code is the stable, neutral identity of the option
 * (ESSENTIAL, PROFESSIONAL, ADVANCED, MAXIMUM), so the label is derived from the code alone and
 * the description is used for nothing but ordering.
 */
function processingOptionLabel(code: string | null | undefined): string {
  const normalized = (code ?? '').trim().toUpperCase();
  if (!normalized) return '';
  return normalized
    .split(/[_\s-]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

function aiPowerSortValue(value: string | null | undefined): number {
  return AI_POWER_SORT_ORDER[(value ?? '').toUpperCase()] ?? Number.MAX_SAFE_INTEGER;
}

function formatPlanPrice(amount: number | null | undefined, currencyCode: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';

  const currency = currencyCode || 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

function offerSortValue(offer: LicencePackageOfferDto): number {
  return offer.displaySortOrder ?? offer.matrixSortOrder ?? offer.baseTierRank ?? Number.MAX_SAFE_INTEGER;
}

function normalizedOfferType(offer: LicencePackageOfferDto): string {
  return (offer.offerType ?? '').replace(/[\s_-]/g, '').toUpperCase();
}

function isBaseOffer(offer: LicencePackageOfferDto): boolean {
  if (normalizedOfferType(offer) === 'BASEPACKAGE') return true;
  const code = (offer.packageCode ?? '').toUpperCase();
  return code.startsWith('QBO-INTAKE-') && !code.includes('ADD') && !code.includes('BUNDLE');
}

function isVendorPortalOffer(offer: LicencePackageOfferDto): boolean {
  const moduleCode = (offer.moduleCode ?? '').toUpperCase();
  const packageCode = (offer.packageCode ?? '').toUpperCase();
  return normalizedOfferType(offer) === 'BASEPACKAGE'
    && moduleCode === 'IERP-VENDOR-PORTAL'
    && packageCode.startsWith('QBO-VP-');
}

function isAddOnOffer(offer: LicencePackageOfferDto): boolean {
  const offerType = normalizedOfferType(offer);
  return offerType === 'ADDON' || offerType === 'BUNDLE' || isVendorPortalOffer(offer);
}

function splitCodes(value: string | null | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
}

function isIncludedWithBase(addOn: LicencePackageOfferDto, base: LicencePackageOfferDto): boolean {
  if (isVendorPortalOffer(addOn)) return false;

  const baseRank = base.baseTierRank ?? 0;
  const includedFromRank = addOn.includedFromTierRank ?? 0;
  if (includedFromRank > 0 && baseRank >= includedFromRank) return true;

  const includedFromPackage = (addOn.includedFromPackage ?? '').toUpperCase();
  return includedFromPackage !== '' && includedFromPackage === (base.packageCode ?? '').toUpperCase();
}

function isAddOnRelevantToBase(addOn: LicencePackageOfferDto, base: LicencePackageOfferDto): boolean {
  // Vendor Portal is independently licensed for the client and can accompany any QBO plan.
  if (isVendorPortalOffer(addOn)) return true;
  if (isIncludedWithBase(addOn, base)) return true;

  const baseCode = (base.packageCode ?? '').toUpperCase();
  const compatible = splitCodes(addOn.compatibleBasePackages);
  if (compatible.length > 0) return compatible.includes(baseCode);

  const minimumRank = addOn.minimumBaseTierRank ?? 0;
  return (base.baseTierRank ?? 0) >= minimumRank;
}

type PlanFeatureRow = {
  code: string;
  label: string;
  value: string;
  explanation?: string | null;
  tooltip?: string | null;
};

type PlanFeatureDefinition = {
  code: string;
  label: string;
  explanation: string;
};

const PLAN_FEATURE_GUIDE: PlanFeatureDefinition[] = [
  {
    code: 'SHARED-PAGES',
    label: 'Monthly Page Allowance',
    explanation: 'The total number of document pages that may be processed each month across enabled page-processing functions for the registered group. Each page of a multi-page document counts toward the allowance; supported transactions received through accepted data exchange do not consume page allowance.',
  },
  {
    code: 'VENDOR-SETUP-INTAKE',
    label: 'Vendor Setup Intake',
    explanation: 'Extracts vendor identity, address, contact and available tax-registration information for review before the vendor is created or updated. Completing the intake establishes vendor context used by later invoice learning.',
  },
  {
    code: 'PURCHASE-INVOICE-INTAKE',
    label: 'Purchase Invoice Intake',
    explanation: 'Reads supplier invoices and prepares invoice header, line, date, amount, tax, reference and recommended accounting information for review while keeping the source document attached to the intake and resulting transaction.',
  },
  {
    code: 'RECOMMENDATION-CARDS',
    label: 'Recommendation Cards',
    explanation: 'Provides a controlled review record for suggested accounting or master-data recommendations before the recommendation is accepted and applied.',
  },
  {
    code: 'VENDOR-INVOICE-OCR',
    label: 'Vendor Invoice OCR',
    explanation: 'Provides enhanced document reading for supplier invoices, especially scanned PDFs, photographs and image-based documents, with extracted information remaining available for review.',
  },
  {
    code: 'INTAKE-AREAS',
    label: 'Intake Areas',
    explanation: 'Provides the landing pages, work queues and review worksheets used to submit, review and approve supported records.',
  },
  {
    code: 'OCR',
    label: 'OCR',
    explanation: 'Reusable document-reading capability for supported iERP AI modules. OCR extracts information for review and does not by itself create or post accounting transactions.',
  },
  {
    code: 'AI-LEARNING',
    label: 'AI Learning',
    explanation: 'Learns from accepted accounting coding to improve future account, dimension, deferral and posting recommendations.',
  },
  {
    code: 'HISTORICAL-LEARNING',
    label: 'Historical Learning',
    explanation: 'Reviews eligible posted history to identify recurring vendor, account, dimension and posting patterns that can support future recommendations.',
  },
  {
    code: 'PREDICTED-POSTING',
    label: 'AI Predicted Posting',
    explanation: 'Recommends accounts, dimensions, deferrals and other supported posting values for review before the transaction is created or posted.',
  },
  {
    code: 'EMAIL-MONITORING',
    label: 'Email Monitoring',
    explanation: 'Routes invoice-like messages and attachments from approved sources into the supported invoice-intake workflow for controlled review.',
  },
  {
    code: 'HANDSHAKES',
    label: 'Handshakes',
    explanation: 'Allows entitled trading partners to establish an accepted connection before supported transaction exchange is enabled.',
  },
  {
    code: 'DATA-EXCHANGE',
    label: 'Data Exchange',
    explanation: 'Allows connected trading partners to exchange supported structured transactions into the receiving company review process instead of requiring duplicate manual entry.',
  },
  {
    code: 'KEY-DIFFERENTIATORS',
    label: 'Key Differentiators',
    explanation: 'Summarizes the principal capabilities and limits that distinguish this package from the other published package tiers.',
  },
];

function presentationRows(offer: LicencePackageOfferDto): PlanFeatureRow[] {
  const rows: Array<[string, string, string | null | undefined]> = [
    ['SHARED-PAGES', 'Monthly Page Allowance', offer.sharedPages],
    ['INTAKE-AREAS', 'Intake Areas', offer.intakeAreas],
    ['OCR', 'OCR', offer.ocr],
    ['RECOMMENDATION-CARDS', 'Recommendation Cards', offer.recommendationCards],
    ['AI-LEARNING', 'AI Learning', offer.aiLearning],
    ['HISTORICAL-LEARNING', 'Historical Learning', offer.historicalLearning],
    ['PREDICTED-POSTING', 'AI Predicted Posting', offer.aiPredictedPosting],
    ['EMAIL-MONITORING', 'Email Monitoring', offer.emailMonitoring],
    ['HANDSHAKES', 'Handshakes', offer.handshakes],
    ['DATA-EXCHANGE', 'Data Exchange', offer.dataExchange],
  ];

  return rows
    .filter((row): row is [string, string, string] => typeof row[2] === 'string' && row[2].trim() !== '')
    .map(([code, label, value]) => ({ code, label, value }));
}

function mergeFeatureRows(matrixRows: PlanFeatureRow[], fallbackRows: PlanFeatureRow[]): PlanFeatureRow[] {
  const merged = new Map<string, PlanFeatureRow>();

  for (const row of matrixRows) merged.set(row.code.toUpperCase(), row);
  for (const row of fallbackRows) {
    const key = row.code.toUpperCase();
    if (!merged.has(key)) merged.set(key, row);
  }

  return [...merged.values()];
}

function completeFeatureRows(matrixRows: PlanFeatureRow[], offer: LicencePackageOfferDto): PlanFeatureRow[] {
  const published = new Map<string, PlanFeatureRow>();
  for (const row of mergeFeatureRows(matrixRows, presentationRows(offer))) {
    published.set(row.code.toUpperCase(), row);
  }

  return PLAN_FEATURE_GUIDE.map((definition) => {
    const row = published.get(definition.code);
    return {
      code: definition.code,
      label: definition.label,
      value: row?.value?.trim() || 'Not specified in the published catalogue',
      explanation: row?.explanation?.trim() || definition.explanation,
      tooltip: row?.tooltip,
    };
  });
}

function addOnExplanation(addOn: LicencePackageOfferDto): string {
  if (isVendorPortalOffer(addOn)) {
    const licensedUsers = addOn.limits?.find(
      (limit) => (limit.operationCode ?? '').toUpperCase() === 'PORTALUSERS',
    )?.limitQuantity;
    const seatText = licensedUsers && licensedUsers > 0 ? `${licensedUsers} licensed vendor users` : 'the published licensed-user tier';
    return `Adds iERP Vendor Portal access for ${seatText}, including the published vendor self-service capabilities for this client.`;
  }

  const featureCode = (addOn.featureCode ?? '').toUpperCase();
  if (featureCode.includes('OCR')) return 'Adds additional OCR/document-reading capability or capacity published for this QuickBooks Online plan.';
  if (featureCode.includes('HISTORICAL')) return 'Adds historical-learning capability published for this QuickBooks Online plan.';
  if (featureCode.includes('LEARNING')) return 'Adds AI-learning capability published for this QuickBooks Online plan.';
  if (featureCode.includes('PREDICTED')) return 'Adds AI predicted-posting capability published for this QuickBooks Online plan.';
  if (featureCode.includes('EMAIL')) return 'Adds email-monitoring capability published for this QuickBooks Online plan.';
  if (featureCode.includes('DATA')) return 'Adds supported structured data-exchange capability published for this QuickBooks Online plan.';
  if ((addOn.packageCode ?? '').toUpperCase().includes('PAGES')) return 'Adds the published document-page capacity to the selected billing period.';
  if ((addOn.packageCode ?? '').toUpperCase().includes('BUNDLE')) return 'Adds the published capability bundle to the selected base package.';
  return addOn.grantedFeatures
    ? `Grants: ${addOn.grantedFeatures}.`
    : 'Optional capability published by iERP licensing for this QuickBooks Online product.';
}

function PlansAndLicencesPanel(): JSX.Element {
  const toast = useToast();
  const { can } = useAuth();
  const canPurchase = can('connection.manage');
  const [searchParams, setSearchParams] = useSearchParams();
  const renewPlanParam = searchParams.get('renewPlan');
  const [expandedPackage, setExpandedPackage] = useState<string | null>(
    renewPlanParam ? renewPlanParam.toUpperCase() : null,
  );
  const [detailPackage, setDetailPackage] = useState<string | null>(null);
  const [planSelections, setPlanSelections] = useState<Record<string, PlanSelection>>({});
  const [selectedAddOns, setSelectedAddOns] = useState<Record<string, string[]>>({});
  const [purchasingPackage, setPurchasingPackage] = useState<string | null>(null);

  const state = useAsync((signal) => licensing.configurator({}, signal), []);

  // Coming from the "renew now" reminder on the expiry banner: open straight to that plan and
  // scroll it into view instead of leaving the person to find it in the table themselves.
  useEffect(() => {
    if (!renewPlanParam) return;
    const row = document.getElementById(`plan-row-${renewPlanParam.toUpperCase()}`);
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('renewPlan');
      return next;
    }, { replace: true });
    // Runs once, right after the plan list first renders with the row already expanded.
  }, [renewPlanParam, state.data]);

  const baseVariants = [...(state.data?.baseOfferVariants ?? [])].filter(isBaseOffer);
  const addOnVariants = [...(state.data?.addOnVariants ?? [])].filter(isAddOnOffer);
  const currentPackage = state.data?.currentSubscription?.packageCode ?? null;
  const currentPower = state.data?.currentSubscription?.aiPowerCode ?? null;
  const currentBilling = state.data?.currentSubscription?.billingPeriod ?? null;
  const currentCurrency = state.data?.currentSubscription?.currencyCode ?? null;
  const recommendedAiPowerByPackage = state.data?.recommendedAiPowerByPackage ?? {};
  const activeAddOnCodes = new Set(
    (state.data?.activeAddOns ?? [])
      .map((addOn) => addOn.packageCode?.toUpperCase())
      .filter((code): code is string => !!code),
  );
  const activeVendorPortalRank = addOnVariants
    .filter(isVendorPortalOffer)
    .filter((offer) => activeAddOnCodes.has((offer.packageCode ?? '').toUpperCase()))
    .reduce((highest, offer) => Math.max(highest, offer.baseTierRank ?? 0), 0);

  const groupedBaseVariants = new Map<string, LicencePackageOfferDto[]>();
  for (const variant of baseVariants) {
    const packageCode = variant.packageCode?.toUpperCase();
    if (!packageCode) continue;
    const variants = groupedBaseVariants.get(packageCode) ?? [];
    variants.push(variant);
    groupedBaseVariants.set(packageCode, variants);
  }

  const variantsForBilling = (variants: LicencePackageOfferDto[], billingPeriod: BillingPeriod) =>
    variants
      .filter((variant) => normalizeBillingPeriod(variant.billingPeriod) === billingPeriod)
      .sort((left, right) => {
        const byPower = aiPowerSortValue(left.aiPowerCode) - aiPowerSortValue(right.aiPowerCode);
        if (byPower !== 0) return byPower;
        return (left.aiPowerDescription ?? '').localeCompare(right.aiPowerDescription ?? '');
      });

  const recommendedPowerFor = (packageCode: string, billingPeriod: BillingPeriod): string | null =>
    recommendedAiPowerByPackage[`${packageCode.toUpperCase()}|${billingPeriod}`]?.toUpperCase() ?? null;

  const defaultVariantFor = (packageCode: string, variants: LicencePackageOfferDto[]): LicencePackageOfferDto => {
    const normalizedPackage = packageCode.toUpperCase();
    const sameCurrentPackage = currentPackage?.toUpperCase() === normalizedPackage;

    if (sameCurrentPackage) {
      const currentVariant = variants.find((variant) =>
        normalizeBillingPeriod(variant.billingPeriod) === normalizeBillingPeriod(currentBilling)
        && (!currentPower || (variant.aiPowerCode ?? '').toUpperCase() === currentPower.toUpperCase()));
      if (currentVariant) return currentVariant;
    }

    const preferredBilling: BillingPeriod = variants.some(
      (variant) => normalizeBillingPeriod(variant.billingPeriod) === 'MONTH',
    ) ? 'MONTH' : normalizeBillingPeriod(variants[0]?.billingPeriod);

    const billingVariants = variantsForBilling(variants, preferredBilling);
    const recommendedPower = recommendedPowerFor(normalizedPackage, preferredBilling);
    if (recommendedPower) {
      const recommended = billingVariants.find(
        (variant) => (variant.aiPowerCode ?? '').toUpperCase() === recommendedPower,
      );
      if (recommended) return recommended;
    }

    return billingVariants[0] ?? variants[0];
  };

  const selectedVariantFor = (packageCode: string, variants: LicencePackageOfferDto[]): LicencePackageOfferDto => {
    const selection = planSelections[packageCode];
    if (selection) {
      const exact = variants.find((variant) =>
        normalizeBillingPeriod(variant.billingPeriod) === selection.billingPeriod
        && (variant.aiPowerCode ?? '').toUpperCase() === selection.aiPowerCode.toUpperCase());
      if (exact) return exact;
    }

    return defaultVariantFor(packageCode, variants);
  };

  const selectBilling = (packageCode: string, variants: LicencePackageOfferDto[], value: string) => {
    const billingPeriod = value === 'YEAR' ? 'YEAR' : 'MONTH';
    const candidates = variantsForBilling(variants, billingPeriod);
    if (candidates.length === 0) return;

    const recommendedPower = recommendedPowerFor(packageCode, billingPeriod);
    const next = (recommendedPower
      ? candidates.find((variant) => (variant.aiPowerCode ?? '').toUpperCase() === recommendedPower)
      : null) ?? candidates[0];

    setPlanSelections((current) => ({
      ...current,
      [packageCode]: {
        billingPeriod,
        aiPowerCode: (next.aiPowerCode ?? '').toUpperCase(),
      },
    }));

    // Add-on prices are billing-period specific. Clear pending selections rather than silently
    // carrying a monthly add-on choice into an annual order or vice versa.
    setSelectedAddOns((current) => ({ ...current, [packageCode]: [] }));
  };

  const selectProcessing = (
    packageCode: string,
    billingPeriod: BillingPeriod,
    value: string,
  ) => {
    setPlanSelections((current) => ({
      ...current,
      [packageCode]: {
        billingPeriod,
        aiPowerCode: value.toUpperCase(),
      },
    }));
  };

  const toggleAddOn = (basePackageCode: string, addOnPackageCode: string) => {
    setSelectedAddOns((current) => {
      const existing = new Set(current[basePackageCode] ?? []);
      const selectedOffer = addOnVariants.find(
        (offer) => (offer.packageCode ?? '').toUpperCase() === addOnPackageCode.toUpperCase(),
      );

      if (existing.has(addOnPackageCode)) {
        existing.delete(addOnPackageCode);
      } else {
        // Vendor Portal is a seat tier, not a stackable feature. Selecting one tier
        // replaces another pending Vendor Portal tier in the same order.
        if (selectedOffer && isVendorPortalOffer(selectedOffer)) {
          for (const existingCode of [...existing]) {
            const existingOffer = addOnVariants.find(
              (offer) => (offer.packageCode ?? '').toUpperCase() === existingCode.toUpperCase(),
            );
            if (existingOffer && isVendorPortalOffer(existingOffer)) existing.delete(existingCode);
          }
        }
        existing.add(addOnPackageCode);
      }

      return { ...current, [basePackageCode]: [...existing] };
    });
  };

  const matrixRowsFor = (offer: LicencePackageOfferDto): PlanFeatureRow[] => {
    const packageCode = offer.packageCode ?? '';
    const row = state.data?.matrixRows?.find(
      (candidate) => (candidate.packageCode ?? '').toUpperCase() === packageCode.toUpperCase(),
    );
    if (!row) return [];

    const valueByColumn = new Map(
      (row.values ?? []).map((value) => [(value.columnCode ?? '').toUpperCase(), value.value ?? '']),
    );

    return [...(state.data?.matrixColumns ?? [])]
      .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
      .map((column) => ({
        code: column.columnCode ?? column.caption ?? 'FEATURE',
        label: column.caption ?? column.columnCode ?? 'Feature',
        value: valueByColumn.get((column.columnCode ?? '').toUpperCase()) ?? '',
        explanation: column.detailedExplanation ?? column.tooltip,
        tooltip: column.tooltip,
      }))
      .filter((rowValue) => rowValue.value.trim() !== '');
  };

  const purchase = async (offer: LicencePackageOfferDto, selected: string[]) => {
    const packageCode = offer.packageCode;
    if (!packageCode) return;

    const billingPeriod = normalizeBillingPeriod(offer.billingPeriod);
    const aiPowerFilter = (offer.aiPowerCode ?? 'RECOMMENDED').toUpperCase();

    setPurchasingPackage(packageCode.toUpperCase());
    try {
      const result = await licensing.purchase({
        packageCode,
        addOnPackageCodes: selected,
        currencyCode: offer.currencyCode ?? state.data?.currencyCode ?? null,
        billingPeriod,
        aiPowerFilter,
      });

      if (result.stripePaymentUrl) {
        window.location.assign(result.stripePaymentUrl);
        return;
      }

      const orderReference = result.salesOrderNo ? ` Sales order ${result.salesOrderNo} was created.` : '';
      const detail = result.lastMessage ?? `iERP did not return a Stripe Checkout URL.${orderReference}`;
      toast.warn('Purchase created, but checkout is not available yet', detail);
      state.reload();
    } catch (error) {
      toast.error('Could not create the licence purchase', errorMessage(error));
    } finally {
      setPurchasingPackage(null);
    }
  };

  const renew = async (offer: LicencePackageOfferDto) => {
    const packageCode = offer.packageCode;
    if (!packageCode) return;

    const billingPeriod = normalizeBillingPeriod(offer.billingPeriod);
    const aiPowerFilter = (offer.aiPowerCode ?? 'RECOMMENDED').toUpperCase();

    setPurchasingPackage(packageCode.toUpperCase());
    try {
      const result = await licensing.purchase({
        packageCode,
        addOnPackageCodes: [],
        currencyCode: offer.currencyCode ?? state.data?.currencyCode ?? null,
        billingPeriod,
        aiPowerFilter,
        renewCurrentPlan: true,
      });

      if (result.stripePaymentUrl) {
        window.location.assign(result.stripePaymentUrl);
        return;
      }

      const orderReference = result.salesOrderNo ? ` Sales order ${result.salesOrderNo} was created.` : '';
      const detail = result.lastMessage ?? `iERP did not return a Stripe Checkout URL.${orderReference}`;
      toast.warn('Renewal created, but checkout is not available yet', detail);
      state.reload();
    } catch (error) {
      toast.error('Could not renew this plan', errorMessage(error));
    } finally {
      setPurchasingPackage(null);
    }
  };

  const planGroups = [...groupedBaseVariants.entries()]
    .map(([packageCode, variants]) => ({
      packageCode,
      variants,
      offer: selectedVariantFor(packageCode, variants),
    }))
    .sort((left, right) => {
      const bySort = offerSortValue(left.offer) - offerSortValue(right.offer);
      if (bySort !== 0) return bySort;
      return (left.offer.description ?? left.packageCode).localeCompare(right.offer.description ?? right.packageCode);
    });

  const detailGroup = detailPackage
    ? planGroups.find((group) => group.packageCode.toUpperCase() === detailPackage.toUpperCase()) ?? null
    : null;
  const detailOffer = detailGroup?.offer ?? null;
  const detailVariants = detailGroup?.variants ?? [];
  const detailNormalizedPackage = detailGroup?.packageCode.toUpperCase() ?? '';
  const detailBilling = detailOffer ? normalizeBillingPeriod(detailOffer.billingPeriod) : 'MONTH';
  const detailPower = (detailOffer?.aiPowerCode ?? '').toUpperCase();
  const detailPrice = detailOffer ? detailOffer.unitPrice ?? detailOffer.baseUnitPrice : null;
  const detailCurrent = detailOffer
    ? currentPackage?.toUpperCase() === detailNormalizedPackage
      && (!currentPower || detailPower === currentPower.toUpperCase())
      && (!currentBilling || detailBilling === normalizeBillingPeriod(currentBilling))
    : false;
  const detailUnavailable = detailOffer ? detailOffer.upgradeEligible === false && !detailCurrent : false;
  const detailBillingOptions = detailOffer
    ? BILLING_PERIOD_OPTIONS.filter((option) =>
      detailVariants.some((variant) => normalizeBillingPeriod(variant.billingPeriod) === option.value))
    : [];
  const detailProcessingVariants = detailOffer
    ? variantsForBilling(detailVariants, detailBilling).filter((variant) => !!variant.aiPowerCode)
    : [];
  const detailProcessingSeen = new Set<string>();
  const detailProcessingOptions = detailProcessingVariants
    .filter((variant) => {
      const code = (variant.aiPowerCode ?? '').toUpperCase();
      if (!code || detailProcessingSeen.has(code)) return false;
      detailProcessingSeen.add(code);
      return true;
    })
    .map((variant) => {
      const code = (variant.aiPowerCode ?? '').toUpperCase();
      const recommended = detailOffer
        ? recommendedPowerFor(detailNormalizedPackage, detailBilling) === code
        : false;
      const label = processingOptionLabel(code);
      return { value: code, label: recommended ? `${label} (Recommended)` : label };
    });
  const detailSelectedAddOns = detailNormalizedPackage
    ? selectedAddOns[detailNormalizedPackage] ?? []
    : [];
  const detailBillingAddOns = detailOffer
    ? addOnVariants
      .filter((addOn) => normalizeBillingPeriod(addOn.billingPeriod) === detailBilling)
      .sort((left, right) => offerSortValue(left) - offerSortValue(right))
    : [];
  const detailApplicableAddOns = detailOffer
    ? detailBillingAddOns.filter((addOn) => isAddOnRelevantToBase(addOn, detailOffer))
    : [];
  const detailMatrixRows = detailOffer ? matrixRowsFor(detailOffer) : [];
  const detailFeatureRows = detailOffer ? completeFeatureRows(detailMatrixRows, detailOffer) : [];
  const detailSelectedAddOnTotal = detailSelectedAddOns.reduce((total, code) => {
    const addOn = detailApplicableAddOns.find(
      (candidate) => candidate.packageCode?.toUpperCase() === code.toUpperCase(),
    );
    return total + (addOn?.unitPrice ?? addOn?.baseUnitPrice ?? 0);
  }, 0);
  const detailBaseEstimatedCharge = detailOffer
    ? detailCurrent ? 0 : (detailOffer.netUpgradeBeforeTax ?? detailPrice ?? 0)
    : 0;
  const detailEstimatedCharge = detailBaseEstimatedCharge + detailSelectedAddOnTotal;
  const detailHasPurchaseChange = !!detailOffer && (!detailCurrent || detailSelectedAddOns.length > 0);

  return (
    <Panel
      title="Plans & licences"
      description="QuickBooks Online plan options are read from the published iERP licence catalogue. Expand a plan to select that plan's billing period, processing option and eligible add-ons before checkout."
      actions={
        <button type="button" className="btn btn-secondary" onClick={state.reload} disabled={state.loading}>
          {state.loading ? 'Refreshing…' : 'Refresh plans'}
        </button>
      }
    >
      <p className="panel-footnote">
        Each plan is configured independently. Only billing and processing combinations published by iERP licensing are offered; prices are not calculated or stored by the QBO Gateway.
      </p>

      {state.loading && !state.data ? (
        <SkeletonText lines={5} />
      ) : state.error && !state.data ? (
        <ErrorState message="Plan options could not be loaded." detail={state.error} onRetry={state.reload} />
      ) : planGroups.length === 0 ? (
        <EmptyState
          title="No QuickBooks Online plan options returned"
          description="The current plan above is still valid. Publish or activate QBO offers in iERP licensing to make additional options available here."
        />
      ) : (
        <TableWrap busy={state.loading}>
          <thead>
            <tr>
              <th scope="col">Plan / option</th>
              <th scope="col">Billing</th>
              <th scope="col">Processing option</th>
              <th scope="col" className="num">Price</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {planGroups.map(({ packageCode, variants, offer }) => {
              const normalizedPackage = packageCode.toUpperCase();
              const selectedBilling = normalizeBillingPeriod(offer.billingPeriod);
              const selectedPower = (offer.aiPowerCode ?? '').toUpperCase();
              const samePackage = currentPackage?.toUpperCase() === normalizedPackage;
              const samePower = currentPower
                ? selectedPower === currentPower.toUpperCase()
                : selectedPower === '';
              const sameBilling = !currentBilling
                || selectedBilling === normalizeBillingPeriod(currentBilling);
              const current = samePackage && samePower && sameBilling;
              const price = offer.unitPrice ?? offer.baseUnitPrice;
              // A trial or a free/$0 plan has no paid term to buy another one of; both stay on the
              // upgrade path rather than offering an action that can't correspond to a real order.
              // Renewal also keeps the current billing currency - an offer priced in another one is
              // a billing change, which the server refuses as a renewal.
              const renewalCurrency = offer.currencyCode ?? state.data?.currencyCode ?? null;
              const sameCurrency = !currentCurrency || !renewalCurrency
                || renewalCurrency.toUpperCase() === currentCurrency.toUpperCase();
              const canRenew = current && !offer.trialPackage && !!price && price > 0 && sameCurrency;
              const unavailable = offer.upgradeEligible === false && !current;
              const expanded = expandedPackage === normalizedPackage;

              const availableBillingPeriods = BILLING_PERIOD_OPTIONS.filter((option) =>
                variants.some((variant) => normalizeBillingPeriod(variant.billingPeriod) === option.value));
              const processingVariants = variantsForBilling(variants, selectedBilling)
                .filter((variant) => !!variant.aiPowerCode);
              const seenProcessing = new Set<string>();
              const processingOptions = processingVariants
                .filter((variant) => {
                  const code = (variant.aiPowerCode ?? '').toUpperCase();
                  if (!code || seenProcessing.has(code)) return false;
                  seenProcessing.add(code);
                  return true;
                })
                .map((variant) => {
                  const code = (variant.aiPowerCode ?? '').toUpperCase();
                  const recommended = recommendedPowerFor(normalizedPackage, selectedBilling) === code;
                  const label = processingOptionLabel(code);
                  return { value: code, label: recommended ? `${label} (Recommended)` : label };
                });

              const applicableAddOns = addOnVariants
                .filter((addOn) => normalizeBillingPeriod(addOn.billingPeriod) === selectedBilling)
                .filter((addOn) => isAddOnRelevantToBase(addOn, offer))
                .sort((left, right) => offerSortValue(left) - offerSortValue(right));
              const selected = selectedAddOns[normalizedPackage] ?? [];
              const matrixRows = matrixRowsFor(offer);
              const featureRows = mergeFeatureRows(matrixRows, presentationRows(offer));
              const selectedAddOnTotal = selected.reduce((total, code) => {
                const addOn = applicableAddOns.find(
                  (candidate) => candidate.packageCode?.toUpperCase() === code.toUpperCase(),
                );
                return total + (addOn?.unitPrice ?? addOn?.baseUnitPrice ?? 0);
              }, 0);
              const baseEstimatedCharge = current ? 0 : (offer.netUpgradeBeforeTax ?? price ?? 0);
              const estimatedCharge = baseEstimatedCharge + selectedAddOnTotal;
              const hasPurchaseChange = !current || selected.length > 0;

              return (
                <Fragment key={normalizedPackage}>
                  <tr id={`plan-row-${normalizedPackage}`}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        aria-expanded={expanded}
                        onClick={() => setExpandedPackage(expanded ? null : normalizedPackage)}
                      >
                        {expanded ? '▾' : '▸'} {offer.description ?? offer.packageCode ?? 'iERP option'}
                      </button>
                      <span className="cell-sub">
                        {[offer.packageCode, offer.offerType].filter(Boolean).join(' · ')}
                      </span>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setDetailPackage(normalizedPackage)}
                      >
                        Plan details & options
                      </button>
                    </td>
                    <td>{billingPeriodLabel(offer.billingPeriod)}</td>
                    <td>{offer.aiPowerDescription ?? 'Included with plan'}</td>
                    <td className="num">{formatPlanPrice(price, offer.currencyCode)}</td>
                    <td>
                      {current ? (
                        <Chip tone="good">Current</Chip>
                      ) : unavailable ? (
                        <Chip tone="neutral">Unavailable</Chip>
                      ) : offer.trialPackage ? (
                        <Chip tone="info">Trial</Chip>
                      ) : (
                        <Chip tone="info">Available</Chip>
                      )}
                      {unavailable && offer.upgradeBlockedReason ? (
                        <span className="cell-sub">{offer.upgradeBlockedReason}</span>
                      ) : null}
                    </td>
                  </tr>

                  {expanded ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="sub-section">
                          <p className="sub-title">Configure this plan</p>
                          <div className="form-grid">
                            <SelectField
                              label="Billing period"
                              value={selectedBilling}
                              onChange={(value) => selectBilling(normalizedPackage, variants, value)}
                              options={availableBillingPeriods}
                              hint="Billing is selected independently for this package."
                            />
                            {processingOptions.length > 0 ? (
                              <SelectField
                                label="Processing option"
                                value={selectedPower}
                                onChange={(value) => selectProcessing(normalizedPackage, selectedBilling, value)}
                                options={processingOptions}
                                hint="Only processing options published as valid for this package are shown."
                              />
                            ) : (
                              <div>
                                <KeyValueList columns={1}>
                                  <KeyValue label="Processing option">
                                    {offer.aiPowerDescription ?? 'Included with plan'}
                                  </KeyValue>
                                </KeyValueList>
                                <p className="panel-footnote">
                                  This package does not expose a separate processing selection.
                                </p>
                              </div>
                            )}
                          </div>
                          <KeyValueList columns={3}>
                            <KeyValue label="Selected billing">{billingPeriodLabel(offer.billingPeriod)}</KeyValue>
                            <KeyValue label="Selected processing">{offer.aiPowerDescription ?? 'Included with plan'}</KeyValue>
                            <KeyValue label="Plan price">{formatPlanPrice(price, offer.currencyCode)}</KeyValue>
                          </KeyValueList>
                        </div>

                        <div className="sub-section">
                          <p className="sub-title">What is included</p>
                          {offer.keyDifferentiators ? <p>{offer.keyDifferentiators}</p> : null}

                          {offer.processingOptionExplanation ? (
                            <div className="sub-section">
                              <strong>Processing option: {offer.aiPowerDescription ?? offer.aiPowerCode ?? 'Published default'}</strong>
                              <p className="panel-footnote">{offer.processingOptionExplanation}</p>
                            </div>
                          ) : null}

                          {featureRows.length > 0 ? (
                            <div>
                              {featureRows.map((feature) => (
                                <details key={feature.code} className="sub-section">
                                  <summary title={feature.tooltip ?? undefined}>
                                    <strong>{feature.label}</strong> · {feature.value}
                                  </summary>
                                  {feature.explanation ? (
                                    <p className="panel-footnote">{feature.explanation}</p>
                                  ) : null}
                                </details>
                              ))}
                            </div>
                          ) : (
                            <p className="panel-footnote">
                              No additional comparison details were published for this plan.
                            </p>
                          )}
                        </div>

                        <div className="sub-section">
                          <p className="sub-title">Add-ons</p>
                          {applicableAddOns.length === 0 ? (
                            <p className="panel-footnote">No separate add-ons apply to this plan.</p>
                          ) : (
                            <div>
                              {applicableAddOns.map((addOn) => {
                                const addOnCode = addOn.packageCode ?? '';
                                const included = isIncludedWithBase(addOn, offer);
                                const active = activeAddOnCodes.has(addOnCode.toUpperCase());
                                const checked = selected.includes(addOnCode);
                                const addOnPrice = addOn.unitPrice ?? addOn.baseUnitPrice;
                                const portalTierNotHigher = isVendorPortalOffer(addOn)
                                  && activeVendorPortalRank > 0
                                  && (addOn.baseTierRank ?? 0) <= activeVendorPortalRank
                                  && !active;

                                return (
                                  <div className="sub-section" key={addOnCode}>
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={included || active || portalTierNotHigher || unavailable || !canPurchase}
                                        onChange={() => toggleAddOn(normalizedPackage, addOnCode)}
                                      />{' '}
                                      <strong>{addOn.description ?? addOnCode}</strong>{' '}
                                      {!included && !active && !portalTierNotHigher
                                        ? formatPlanPrice(addOnPrice, addOn.currencyCode ?? offer.currencyCode)
                                        : ''}
                                    </label>
                                    {included ? <Chip tone="good">Included with this plan</Chip> : null}
                                    {active ? <Chip tone="good">Already active</Chip> : null}
                                    {portalTierNotHigher ? <Chip tone="neutral">Current or lower Vendor Portal tier</Chip> : null}
                                    {addOn.prerequisiteFeatures ? (
                                      <span className="cell-sub">Requires: {addOn.prerequisiteFeatures}</span>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="sub-section">
                          <p className="sub-title">Order summary</p>
                          <KeyValueList columns={3}>
                            <KeyValue label={current ? 'Current plan charge' : 'Plan / upgrade'}>
                              {formatPlanPrice(baseEstimatedCharge, offer.currencyCode)}
                            </KeyValue>
                            <KeyValue label="Selected add-ons">
                              {formatPlanPrice(selectedAddOnTotal, offer.currencyCode)}
                            </KeyValue>
                            <KeyValue label="Estimated before tax">
                              {formatPlanPrice(estimatedCharge, offer.currencyCode)}
                            </KeyValue>
                          </KeyValueList>
                          <p className="panel-footnote">
                            Final price, upgrade credit and tax are recalculated by Business Central when the order is created. Card details are entered only on Stripe Checkout.
                          </p>

                          {canPurchase ? (
                            <div className="form-actions">
                              {canRenew && selected.length === 0 ? (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  disabled={unavailable || purchasingPackage !== null}
                                  onClick={() => void renew(offer)}
                                >
                                  {purchasingPackage === normalizedPackage
                                    ? 'Creating checkout…'
                                    : `Renew this plan (buy the next ${billingPeriodLabel(offer.billingPeriod).toLowerCase()})`}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  disabled={unavailable || !hasPurchaseChange || purchasingPackage !== null}
                                  onClick={() => void purchase(offer, selected)}
                                >
                                  {purchasingPackage === normalizedPackage
                                    ? 'Creating checkout…'
                                    : current
                                      ? selected.length > 0
                                        ? 'Purchase selected add-ons'
                                        : 'Current plan'
                                      : 'Continue to secure checkout'}
                                </button>
                              )}
                            </div>
                          ) : (
                            <p className="panel-footnote">An administrator can purchase or change licence options.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </TableWrap>
      )}

      <Drawer
        open={detailOffer !== null}
        onClose={() => setDetailPackage(null)}
        title={detailOffer?.description ?? 'Plan details & options'}
        footer={
          detailOffer ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDetailPackage(null)}
                disabled={purchasingPackage !== null}
              >
                Close
              </button>
              {canPurchase ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={detailUnavailable || !detailHasPurchaseChange || purchasingPackage !== null}
                  onClick={() => void purchase(detailOffer, detailSelectedAddOns)}
                >
                  {purchasingPackage === detailNormalizedPackage
                    ? 'Creating checkout…'
                    : detailCurrent
                      ? detailSelectedAddOns.length > 0
                        ? 'Purchase selected add-ons'
                        : 'Current plan'
                      : 'Select plan & continue to checkout'}
                </button>
              ) : null}
            </>
          ) : null
        }
      >
        {detailOffer ? (
          <>
            <p className="panel-footnote">
              Review this package, its published price and every available option without leaving Settings.
              Final commercial validation, upgrade credit and tax remain authoritative when checkout is created.
            </p>

            <KeyValueList columns={3}>
              <KeyValue label="Plan">{detailOffer.description ?? detailOffer.packageCode ?? 'iERP plan'}</KeyValue>
              <KeyValue label="Published price">{formatPlanPrice(detailPrice, detailOffer.currencyCode)}</KeyValue>
              <KeyValue label="Status">
                {detailCurrent ? 'Current' : detailUnavailable ? 'Unavailable' : detailOffer.trialPackage ? 'Trial' : 'Available'}
              </KeyValue>
            </KeyValueList>

            {detailOffer.keyDifferentiators ? (
              <div className="sub-section">
                <p className="sub-title">Package summary</p>
                <p>{detailOffer.keyDifferentiators}</p>
              </div>
            ) : null}

            <div className="sub-section">
              <p className="sub-title">Choose this package</p>
              <div className="form-grid">
                <SelectField
                  label="Billing period"
                  value={detailBilling}
                  onChange={(value) => selectBilling(detailNormalizedPackage, detailVariants, value)}
                  options={detailBillingOptions}
                  hint="The price shown below is the published price for this billing selection."
                />
                {detailProcessingOptions.length > 0 ? (
                  <SelectField
                    label="Processing option"
                    value={detailPower}
                    onChange={(value) => selectProcessing(detailNormalizedPackage, detailBilling, value)}
                    options={detailProcessingOptions}
                    hint="Only processing options published for this package are selectable."
                  />
                ) : (
                  <KeyValueList columns={1}>
                    <KeyValue label="Processing option">
                      {detailOffer.aiPowerDescription ?? 'Included with plan'}
                    </KeyValue>
                  </KeyValueList>
                )}
              </div>
              <KeyValueList columns={3}>
                <KeyValue label="Billing">{billingPeriodLabel(detailBilling)}</KeyValue>
                <KeyValue label="Processing">{detailOffer.aiPowerDescription ?? 'Included with plan'}</KeyValue>
                <KeyValue label="Plan price">{formatPlanPrice(detailPrice, detailOffer.currencyCode)}</KeyValue>
              </KeyValueList>
              {detailOffer.processingOptionExplanation ? (
                <p className="panel-footnote">{detailOffer.processingOptionExplanation}</p>
              ) : null}
            </div>

            <div className="sub-section">
              <p className="sub-title">What this plan includes</p>
              <p className="panel-footnote">
                Every standard capability is explained below. Values come from the published catalogue when supplied;
                a missing value is shown explicitly rather than guessed by the Gateway.
              </p>
              {detailFeatureRows.map((feature) => (
                <details key={feature.code} className="sub-section">
                  <summary title={feature.tooltip ?? undefined}>
                    <strong>{feature.label}</strong> · {feature.value}
                  </summary>
                  <p className="panel-footnote">{feature.explanation}</p>
                </details>
              ))}
            </div>

            <div className="sub-section">
              <p className="sub-title">Add-ons and additional options</p>
              {detailBillingAddOns.some(isVendorPortalOffer) ? (
                <p className="panel-footnote">
                  Vendor Portal is licensed separately by active vendor-user tier. Select one Vendor Portal tier; it does not change this plan's AI processing option.
                </p>
              ) : null}
              {detailBillingAddOns.length === 0 ? (
                <p className="panel-footnote">
                  No QuickBooks Online add-on products were returned by the published catalogue for this billing period.
                </p>
              ) : (
                detailBillingAddOns.map((addOn) => {
                  const addOnCode = addOn.packageCode ?? '';
                  const relevant = isAddOnRelevantToBase(addOn, detailOffer);
                  const included = relevant && isIncludedWithBase(addOn, detailOffer);
                  const active = activeAddOnCodes.has(addOnCode.toUpperCase());
                  const checked = detailSelectedAddOns.includes(addOnCode);
                  const addOnPrice = addOn.unitPrice ?? addOn.baseUnitPrice;
                  const portalTierNotHigher = isVendorPortalOffer(addOn)
                    && activeVendorPortalRank > 0
                    && (addOn.baseTierRank ?? 0) <= activeVendorPortalRank
                    && !active;
                  const selectable = relevant
                    && !included
                    && !active
                    && !portalTierNotHigher
                    && !detailUnavailable
                    && canPurchase;

                  return (
                    <div className="sub-section" key={addOnCode}>
                      <label>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!selectable}
                          onChange={() => toggleAddOn(detailNormalizedPackage, addOnCode)}
                        />{' '}
                        <strong>{addOn.description ?? addOnCode}</strong>{' '}
                        {relevant && !included && !active && !portalTierNotHigher
                          ? formatPlanPrice(addOnPrice, addOn.currencyCode ?? detailOffer.currencyCode)
                          : ''}
                      </label>
                      <span className="cell-sub">{addOnExplanation(addOn)}</span>
                      {included ? <Chip tone="good">Included with this plan</Chip> : null}
                      {active ? <Chip tone="good">Already active</Chip> : null}
                      {portalTierNotHigher ? <Chip tone="neutral">Current or lower Vendor Portal tier</Chip> : null}
                      {!relevant ? <Chip tone="neutral">Not available with this plan</Chip> : null}
                      {addOn.prerequisiteFeatures ? (
                        <span className="cell-sub">Requires: {addOn.prerequisiteFeatures}</span>
                      ) : null}
                      {addOn.grantedFeatures ? (
                        <span className="cell-sub">Includes: {addOn.grantedFeatures}</span>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>

            <div className="sub-section">
              <p className="sub-title">Selection summary</p>
              <KeyValueList columns={3}>
                <KeyValue label={detailCurrent ? 'Current plan charge' : 'Plan / upgrade'}>
                  {formatPlanPrice(detailBaseEstimatedCharge, detailOffer.currencyCode)}
                </KeyValue>
                <KeyValue label="Selected add-ons">
                  {formatPlanPrice(detailSelectedAddOnTotal, detailOffer.currencyCode)}
                </KeyValue>
                <KeyValue label="Estimated before tax">
                  {formatPlanPrice(detailEstimatedCharge, detailOffer.currencyCode)}
                </KeyValue>
              </KeyValueList>
              <p className="panel-footnote">
                The browser does not submit a commercial price. The exact selected plan and add-on codes are revalidated
                before the authoritative order, tax and Stripe Checkout are created.
              </p>
              {!canPurchase ? (
                <p className="panel-footnote">An administrator can purchase or change licence options.</p>
              ) : null}
            </div>
          </>
        ) : null}
      </Drawer>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// QuickBooks connection
// ---------------------------------------------------------------------------

function ConnectionCard(): JSX.Element {
  const toast = useToast();
  const { can } = useAuth();
  const canManage = can('connection.manage');
  const state = useAsync((signal) => qbo.connection(signal), []);
  const [busy, setBusy] = useState<'connect' | 'test' | 'disconnect' | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const connect = async () => {
    setBusy('connect');
    try {
      const response = await qbo.connect(`${window.location.origin}/settings`);
      // The gateway hands back Intuit's consent URL; leave the SPA to complete OAuth.
      window.location.assign(response.authorizationUrl);
    } catch (error) {
      toast.error('Could not start the QuickBooks connection', errorMessage(error));
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy('test');
    try {
      const result = await qbo.test();
      if (result) state.setData(result);
      if (result?.isConnected) {
        toast.success('QuickBooks responded', result.companyName ?? 'The connection is healthy.');
      } else {
        toast.warn('QuickBooks did not respond as connected', result?.lastError ?? undefined);
      }
    } catch (error) {
      toast.error('Connection test failed', errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    setBusy('disconnect');
    try {
      await qbo.disconnect();
      toast.success('Disconnected', 'The gateway will no longer call QuickBooks Online.');
      setConfirmDisconnect(false);
      state.reload();
    } catch (error) {
      toast.error('Disconnect failed', errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  if (state.loading && !state.data) {
    return (
      <Panel title="QuickBooks Online connection">
        <SkeletonText lines={5} />
      </Panel>
    );
  }

  if (state.error && !state.data) {
    return (
      <Panel title="QuickBooks Online connection">
        <ErrorState message="Connection status unavailable." detail={state.error} onRetry={state.reload} />
      </Panel>
    );
  }

  const connection = state.data;
  if (!connection) return <EmptyState title="No connection information" />;

  const reconsentSoon =
    connection.daysUntilReconsentRequired !== null && connection.daysUntilReconsentRequired <= 14;

  return (
    <Panel
      title="QuickBooks Online connection"
      description="OAuth tokens are held by the gateway; refresh tokens expire 100 days after consent."
      actions={
        canManage ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={test} disabled={busy !== null}>
              {busy === 'test' ? 'Testing…' : 'Test'}
            </button>
            {connection.isConnected ? (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setConfirmDisconnect(true)}
                disabled={busy !== null}
              >
                Disconnect
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={connect} disabled={busy !== null}>
                {busy === 'connect' ? 'Redirecting…' : 'Connect to QuickBooks'}
              </button>
            )}
          </>
        ) : undefined
      }
    >
      <div className="connection-head">
        <ConnectionStatusChip status={connection.status} />
        {connection.isSandbox ? <Chip tone="warn">Sandbox company</Chip> : <Chip tone="neutral">Production</Chip>}
        {connection.isConnected ? <Chip tone="good">Tokens present</Chip> : null}
      </div>

      {reconsentSoon ? (
        <div className="banner banner-warn banner-inline" role="alert">
          <div className="banner-body">
            <p className="banner-title">
              Re-consent required in {connection.daysUntilReconsentRequired} days
            </p>
            <p className="banner-detail">
              Intuit refresh tokens expire 100 days after consent. Reconnect before then or the gateway
              will stop posting.
            </p>
          </div>
          {canManage ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={connect} disabled={busy !== null}>
              Reconnect
            </button>
          ) : null}
        </div>
      ) : null}

      {connection.status === QboConnectionStatus.Error && connection.lastError ? (
        <div className="banner banner-bad banner-inline" role="alert">
          <div className="banner-body">
            <p className="banner-title">Last QuickBooks call failed</p>
            <p className="banner-detail">{connection.lastError}</p>
          </div>
        </div>
      ) : null}

      <KeyValueList columns={3}>
        <KeyValue label="Company">{connection.companyName ?? '—'}</KeyValue>
        <KeyValue label="Realm id">
          <span className="mono">{connection.realmId ?? '—'}</span>
        </KeyValue>
        <KeyValue label="Home currency">{connection.homeCurrencyCode ?? '—'}</KeyValue>
        <KeyValue label="Country">{connection.countryCode ?? '—'}</KeyValue>
        <KeyValue label="Connected">
          {connection.connectedUtc ? formatDateTime(connection.connectedUtc) : '—'}
        </KeyValue>
        <KeyValue label="Connected by">{connection.connectedByEmail ?? '—'}</KeyValue>
        <KeyValue label="Access token expires">
          {connection.accessTokenExpiresUtc ? formatRelative(connection.accessTokenExpiresUtc) : '—'}
        </KeyValue>
        <KeyValue label="Refresh token expires">
          {connection.refreshTokenExpiresUtc ? formatDateTime(connection.refreshTokenExpiresUtc) : '—'}
        </KeyValue>
        <KeyValue label="Last successful call">
          {connection.lastSuccessfulCallUtc ? formatRelative(connection.lastSuccessfulCallUtc) : '—'}
        </KeyValue>
      </KeyValueList>

      <ConfirmDialog
        open={confirmDisconnect}
        title="Disconnect QuickBooks Online"
        message="The gateway will stop reading from and writing to this company until someone reconnects. Queued documents will fail to post."
        confirmLabel="Disconnect"
        tone="danger"
        pending={busy === 'disconnect'}
        onCancel={() => setConfirmDisconnect(false)}
        onConfirm={() => void disconnect()}
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

interface UserDraft {
  email: string;
  displayName: string;
  role: GatewayRole;
  password: string;
  approvalLimit: string;
  isActive: boolean;
}

const EMPTY_USER: UserDraft = {
  email: '',
  displayName: '',
  role: GatewayRole.Operator,
  password: '',
  approvalLimit: '',
  isActive: true,
};

function UsersPanel(): JSX.Element {
  const toast = useToast();
  const { user: currentUser, refreshUser } = useAuth();
  const list = useAsync((signal) => users.list(signal), []);

  const [editing, setEditing] = useState<UserDto | 'new' | null>(null);
  const [draft, setDraft] = useState<UserDraft>(EMPTY_USER);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (editing === 'new') {
      setDraft(EMPTY_USER);
    } else if (editing) {
      setDraft({
        email: editing.email,
        displayName: editing.displayName,
        role: editing.role,
        password: '',
        approvalLimit: editing.approvalLimit === null ? '' : String(editing.approvalLimit),
        isActive: editing.isActive,
      });
    }
    setSaveError(null);
  }, [editing]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      if (editing === 'new') {
        if (draft.email.trim() === '' || draft.displayName.trim() === '') {
          setSaveError('Email and display name are required.');
          setSaving(false);
          return;
        }
        await users.create({
          email: draft.email.trim(),
          displayName: draft.displayName.trim(),
          role: draft.role,
          password: draft.password.trim() || null,
          approvalLimit: parseDecimalInput(draft.approvalLimit),
        });
        toast.success('User created', draft.email.trim());
      } else if (editing) {
        await users.update(editing.id, {
          displayName: draft.displayName.trim() || null,
          role: draft.role,
          approvalLimit: parseDecimalInput(draft.approvalLimit),
          isActive: draft.isActive,
        });
        toast.success('User updated', editing.email);
        if (currentUser && currentUser.id === editing.id) {
          // Our own role or limit may have changed; refresh the cached identity.
          await refreshUser().catch(() => undefined);
        }
      }
      setEditing(null);
      list.reload();
    } catch (error) {
      const message = errorMessage(error);
      setSaveError(message);
      toast.error('Save failed', message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel
      title="Users"
      description="Roles decide what each person can do in the gateway."
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
          Add user
        </button>
      }
    >
      {list.loading && !list.data ? (
        <TableSkeleton rows={5} columns={5} />
      ) : list.error ? (
        <ErrorState message="Users could not be loaded." detail={list.error} onRetry={list.reload} />
      ) : !list.data || list.data.length === 0 ? (
        <EmptyState title="No users" description="Invite the first operator to get started." />
      ) : (
        <TableWrap busy={list.loading}>
          <thead>
            <tr>
              <th scope="col">User</th>
              <th scope="col">Role</th>
              <th scope="col" className="num">
                Approval limit
              </th>
              <th scope="col">Last sign-in</th>
              <th scope="col">Status</th>
              <th scope="col" className="col-actions">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.data.map((user) => (
              <tr key={user.id} className={user.isActive ? '' : 'row-muted'}>
                <td>
                  <span className="cell-strong">{user.displayName}</span>
                  <span className="cell-sub">{user.email}</span>
                </td>
                <td>
                  <Chip tone={user.role === GatewayRole.Admin ? 'info' : 'neutral'}>
                    {GatewayRoleLabels[user.role]}
                  </Chip>
                </td>
                <td className="num">
                  {user.approvalLimit === null ? 'No limit' : formatMoney(user.approvalLimit)}
                </td>
                <td>{user.lastSignInUtc ? formatRelative(user.lastSignInUtc) : 'never'}</td>
                <td>
                  <Chip tone={user.isActive ? 'good' : 'neutral'}>{user.isActive ? 'Active' : 'Disabled'}</Chip>
                </td>
                <td className="col-actions">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(user)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      <Drawer
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add user' : 'Edit user'}
        width="narrow"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save user'}
            </button>
          </>
        }
      >
        <InlineError message={saveError} />

        <TextField
          label="Email"
          type="email"
          value={draft.email}
          onChange={(value) => setDraft((current) => ({ ...current, email: value }))}
          required
          readOnly={editing !== 'new'}
          hint={editing !== 'new' ? 'Email addresses cannot be changed.' : undefined}
        />
        <TextField
          label="Display name"
          value={draft.displayName}
          onChange={(value) => setDraft((current) => ({ ...current, displayName: value }))}
          required
        />
        <SelectField
          label="Role"
          value={String(draft.role)}
          onChange={(value) =>
            setDraft((current) => ({
              ...current,
              role: parseEnumValue<GatewayRole>(value) ?? GatewayRole.Auditor,
            }))
          }
          options={enumOptions(GatewayRoleLabels)}
          hint={GatewayRoleDescriptions[draft.role]}
        />
        <TextField
          label="Approval limit"
          type="number"
          inputMode="decimal"
          value={draft.approvalLimit}
          onChange={(value) => setDraft((current) => ({ ...current, approvalLimit: value }))}
          placeholder="Blank for no limit"
          hint="Approvals above this amount are blocked for this user."
        />
        {editing === 'new' ? (
          <TextField
            label="Initial password"
            type="password"
            value={draft.password}
            onChange={(value) => setDraft((current) => ({ ...current, password: value }))}
            autoComplete="new-password"
            hint="Leave blank to have the gateway send an invitation instead."
          />
        ) : (
          <ToggleField
            label="Account is active"
            checked={draft.isActive}
            onChange={(checked) => setDraft((current) => ({ ...current, isActive: checked }))}
          />
        )}
      </Drawer>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Team (pending invitations)
// ---------------------------------------------------------------------------

interface InviteDraft {
  email: string;
  displayName: string;
  role: GatewayRole;
  approvalLimit: string;
}

const EMPTY_INVITE: InviteDraft = {
  email: '',
  displayName: '',
  role: GatewayRole.Operator,
  approvalLimit: '',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function TeamPanel(): JSX.Element {
  const toast = useToast();
  const list = useAsync((signal) => invitations.list(signal), []);

  const [draft, setDraft] = useState<InviteDraft>(EMPTY_INVITE);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<InvitationDto | null>(null);

  const pending = (list.data ?? []).filter((invitation) => invitation.isOpen);

  const invite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    if (!EMAIL_PATTERN.test(draft.email.trim())) {
      setFormError('Enter the email address to invite.');
      return;
    }

    setSending(true);
    try {
      const created = await invitations.create({
        email: draft.email.trim(),
        displayName: draft.displayName.trim() || null,
        role: draft.role,
        approvalLimit: parseDecimalInput(draft.approvalLimit),
      });
      toast.success('Invitation sent', created?.email ?? draft.email.trim());
      setDraft(EMPTY_INVITE);
      list.reload();
    } catch (error) {
      const message = errorMessage(error);
      setFormError(message);
      toast.error('Invitation failed', message);
    } finally {
      setSending(false);
    }
  };

  const resend = async (invitation: InvitationDto) => {
    setBusyId(invitation.id);
    try {
      await invitations.resend(invitation.id);
      toast.success('Invitation resent', invitation.email);
      list.reload();
    } catch (error) {
      toast.error('Could not resend the invitation', errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (invitation: InvitationDto) => {
    setBusyId(invitation.id);
    try {
      await invitations.revoke(invitation.id);
      toast.success('Invitation revoked', `${invitation.email} can no longer use that link.`);
      setConfirmRevoke(null);
      list.reload();
    } catch (error) {
      toast.error('Could not revoke the invitation', errorMessage(error));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Panel
      title="Team"
      description="Invitations let someone set their own password. Nothing is created until they accept."
      actions={
        <button type="button" className="btn btn-secondary" onClick={list.reload} disabled={list.loading}>
          {list.loading ? 'Refreshing…' : 'Refresh'}
        </button>
      }
    >
      <div className="sub-section">
        <div className="sub-head">
          <h3 className="sub-title">Pending invitations</h3>
        </div>

        {list.loading && !list.data ? (
          <TableSkeleton rows={3} columns={5} />
        ) : list.error ? (
          <ErrorState message="Invitations could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : pending.length === 0 ? (
          <EmptyState
            title="No pending invitations"
            description="Invite someone below and their invitation will appear here until they accept it."
            icon="✉"
          />
        ) : (
          <TableWrap busy={list.loading}>
            <thead>
              <tr>
                <th scope="col">Invitee</th>
                <th scope="col">Role</th>
                <th scope="col" className="num">
                  Approval limit
                </th>
                <th scope="col">Invited by</th>
                <th scope="col">Expires</th>
                <th scope="col" className="col-actions">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pending.map((invitation) => (
                <tr key={invitation.id}>
                  <td>
                    <span className="cell-strong">{invitation.email}</span>
                    <span className="cell-sub">
                      {invitation.displayName ?? 'No name given'} · sent{' '}
                      {formatRelative(invitation.createdUtc)}
                    </span>
                  </td>
                  <td>
                    <Chip tone={invitation.role === GatewayRole.Admin ? 'info' : 'neutral'}>
                      {GatewayRoleLabels[invitation.role]}
                    </Chip>
                  </td>
                  <td className="num">
                    {invitation.approvalLimit === null
                      ? 'No limit'
                      : formatMoney(invitation.approvalLimit)}
                  </td>
                  <td>{invitation.invitedByEmail ?? '—'}</td>
                  <td>
                    {formatRelative(invitation.expiresUtc)}
                    <span className="cell-sub">{formatDate(invitation.expiresUtc)}</span>
                  </td>
                  <td className="col-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void resend(invitation)}
                      disabled={busyId !== null}
                    >
                      {busyId === invitation.id ? 'Working…' : 'Resend'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setConfirmRevoke(invitation)}
                      disabled={busyId !== null}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </div>

      <form className="sub-section" onSubmit={invite} noValidate>
        <div className="sub-head">
          <h3 className="sub-title">Invite someone</h3>
        </div>

        <div className="form-grid">
          <TextField
            label="Email"
            type="email"
            value={draft.email}
            onChange={(value) => setDraft((current) => ({ ...current, email: value }))}
            required
            autoComplete="off"
            placeholder="colleague@company.com"
          />
          <TextField
            label="Display name"
            value={draft.displayName}
            onChange={(value) => setDraft((current) => ({ ...current, displayName: value }))}
            placeholder="Optional — they can set it themselves"
          />
          <SelectField
            label="Role"
            value={String(draft.role)}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                role: parseEnumValue<GatewayRole>(value) ?? GatewayRole.Auditor,
              }))
            }
            options={enumOptions(GatewayRoleLabels)}
            hint={GatewayRoleDescriptions[draft.role]}
          />
          <TextField
            label="Approval limit"
            type="number"
            inputMode="decimal"
            value={draft.approvalLimit}
            onChange={(value) => setDraft((current) => ({ ...current, approvalLimit: value }))}
            placeholder="Blank for no limit"
            hint="Approvals above this amount are blocked for this person."
          />
        </div>

        <InlineError message={formError} />

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={sending}>
            {sending ? 'Sending…' : 'Send invitation'}
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmRevoke !== null}
        title="Revoke invitation"
        message={
          confirmRevoke
            ? `${confirmRevoke.email} will no longer be able to use the link we emailed. You can invite them again at any time.`
            : ''
        }
        confirmLabel="Revoke"
        tone="danger"
        pending={busyId !== null && confirmRevoke !== null && busyId === confirmRevoke.id}
        onCancel={() => setConfirmRevoke(null)}
        onConfirm={() => {
          if (confirmRevoke) void revoke(confirmRevoke);
        }}
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Change password
// ---------------------------------------------------------------------------

/**
 * THE CONTROLS THAT ONLY EXIST BECAUSE SEVERAL PEOPLE SHARE ONE QUICKBOOKS COMPANY.
 *
 * Read by anyone signed in on purpose: these rules constrain the people working here, and a control
 * somebody can be refused by but never shown is how "the system randomly would not let me post"
 * support calls happen. Only an administrator can change them, which the API enforces too.
 *
 * Both default to OFF. Turning a control on has to be a decision somebody made, rather than
 * something that silently started refusing work after an upgrade.
 */
function CompanyAccessPanel(): JSX.Element {
  const toast = useToast();
  const { user } = useAuth();
  const canWrite = (user?.role ?? GatewayRole.Auditor) >= GatewayRole.Admin;

  const policy = useAsync((signal) => companyAccess.get(signal), []);

  const [separate, setSeparate] = useState(false);
  const [ceiling, setCeiling] = useState('');
  const [requireClaim, setRequireClaim] = useState(false);
  const [claimMinutes, setClaimMinutes] = useState('30');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!policy.data) return;
    setSeparate(policy.data.requireSeparateApprover);
    setCeiling(
      policy.data.selfApprovalAllowedUpTo === null
        ? ''
        : String(policy.data.selfApprovalAllowedUpTo),
    );
    setRequireClaim(policy.data.requireReviewClaim);
    setClaimMinutes(String(policy.data.reviewClaimMinutes));
  }, [policy.data]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await companyAccess.update({
        requireSeparateApprover: separate,
        // A ceiling only means something alongside the rule it relaxes, and the API refuses one
        // without it rather than storing a value that does nothing.
        selfApprovalAllowedUpTo: separate ? parseDecimalInput(ceiling) : null,
        requireReviewClaim: requireClaim,
        reviewClaimMinutes: Number(claimMinutes) || 30,
      });
      policy.setData(updated);
      toast.success('Saved', 'These rules apply from the next action anybody takes.');
    } catch (cause) {
      const message = errorMessage(cause);
      setError(message);
      toast.error('Could not save', message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel
      title="Working as a team"
      description="Who may approve what, and whether review work is claimed before it is edited."
    >
      {policy.error ? (
        <ErrorState message={policy.error} onRetry={policy.reload} />
      ) : (
        <>
          {policy.data?.isDefault ? (
            <p className="panel-footnote">
              Nobody has set these yet, so the defaults below apply: both controls off.
            </p>
          ) : policy.data?.modifiedByEmail ? (
            <p className="panel-footnote">
              Last changed by {policy.data.modifiedByEmail} on{' '}
              {formatDateTime(policy.data.modifiedUtc)}.
            </p>
          ) : null}

          <ToggleField
            label="Require a second person to approve"
            checked={separate}
            onChange={setSeparate}
            disabled={!canWrite || saving}
          />
          <p className="panel-footnote">
            Whoever last changed the accounting detail of a version cannot approve that version. It
            is not the requester who is blocked — somebody can refer a colleague&apos;s invoice — and
            an administrator is not exempt, because this control exists precisely for the person who
            has enough authority to do both halves.
          </p>

          {separate ? (
            <TextField
              label="Allow self-approval up to"
              value={ceiling}
              onChange={setCeiling}
              placeholder="Leave blank for no exception"
              hint="A small-value exception, in the company currency. Blank is the stricter reading."
              disabled={!canWrite || saving}
            />
          ) : null}

          <ToggleField
            label="Claim an invoice before editing it"
            checked={requireClaim}
            onChange={setRequireClaim}
            disabled={!canWrite || saving}
          />
          <p className="panel-footnote">
            Stops two people unknowingly working the same invoice. A claim is a courtesy lock over
            editing and nothing more: it is never read by the posting gate, the approval limit check
            or the approval-required rule, and holding one does not make anybody an approver.
          </p>

          <TextField
            label="A claim lapses after"
            value={claimMinutes}
            onChange={setClaimMinutes}
            hint="Minutes, between 5 and 480. A claim that never lapsed would strand work behind whoever closed their laptop first."
            disabled={!canWrite || saving}
          />

          <InlineError message={error} />

          {canWrite ? (
            <div className="action-bar">
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          ) : (
            <p className="panel-footnote">
              Only an administrator can change these. They are shown here because they constrain what
              you can do.
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

function ChangePasswordPanel(): JSX.Element {
  const toast = useToast();
  const policy = usePasswordPolicy();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assessment = assessPassword(newPassword, policy);
  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const reused = newPassword.length > 0 && newPassword === currentPassword;
  const canSubmit =
    currentPassword !== '' && assessment.satisfied && confirm === newPassword && !reused;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (!canSubmit) return;

    setSaving(true);
    try {
      await account.changePassword({ currentPassword, newPassword });
      toast.success('Password changed', 'Use the new password next time you sign in.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      setSubmitted(false);
    } catch (cause) {
      const message = errorMessage(cause);
      setError(message);
      toast.error('Password not changed', message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel
      title="Change password"
      description="Your current password is required, so a borrowed session cannot lock you out."
    >
      <form className="password-form" onSubmit={submit} noValidate>
        <PasswordField
          label="Current password"
          value={currentPassword}
          onChange={setCurrentPassword}
          autoComplete="current-password"
          required
          error={submitted && currentPassword === '' ? 'Enter your current password.' : null}
        />
        <PasswordField
          label="New password"
          value={newPassword}
          onChange={setNewPassword}
          autoComplete="new-password"
          required
          error={
            reused
              ? 'Choose a password you are not already using.'
              : submitted && !assessment.satisfied
                ? 'This password does not meet the policy.'
                : null
          }
        />
        <PasswordField
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          required
          error={
            mismatch || (submitted && confirm !== newPassword)
              ? 'The two passwords do not match.'
              : null
          }
        />

        <PasswordStrength password={newPassword} policy={policy} />

        <InlineError message={error} />

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Updating…' : 'Change password'}
          </button>
        </div>
      </form>
    </Panel>
  );
}

export function SettingsPage(): JSX.Element {
  const { data } = useOnboarding();

  // One shared predicate with the API and the route guards, so Settings can never show panels the
  // server would refuse. Fail closed: while the state is unknown nothing gated is shown — no
  // locked-panel flash for a verified tenant, and no feature flash for an unverified one.
  const state = tenantFeatureState(data);

  return (
    <>
      <PageHeader
        title="Settings"
        description="Your plan and usage, connection ownership, gateway users and your own password."
      />
      <PlanUsageCard />
      {state === 'enabled' ? (
        <>
          <PlansAndLicencesPanel />
          <LicencePurchasesPanel />
          <ConnectionCard />
          <CompanyAccessPanel />
          <RequireRole minRole={GatewayRole.Admin} fallback={null}>
            <UsersPanel />
          </RequireRole>
          <RequireRole minRole={GatewayRole.Admin} fallback={null}>
            <TeamPanel />
          </RequireRole>
        </>
      ) : state === 'unverified' ? (
        <Panel
          title="Locked until your licence is verified"
          description="The QuickBooks connection and user management unlock once the iERP AI licence is verified."
        >
          <p className="panel-footnote">
            Open the verification email iERP sent — or resend it from the dashboard — then come back
            here to connect QuickBooks and invite your team.
          </p>
        </Panel>
      ) : state === 'licence-inactive' ? (
        <>
          {/* A lapsed licence is ended by renewing it, so the plans and purchases stay usable -
              the API opens exactly these to a lapsed tenant ([AllowLapsedLicence]). */}
          <PlansAndLicencesPanel />
          <LicencePurchasesPanel />
          <Panel
            title="Locked while the licence is inactive"
            description="The QuickBooks connection and user management are locked until the licence is active again."
          >
            <p className="panel-footnote">
              Renew the plan above to unlock them, or contact iERP. Everything already posted stays
              exactly as it is.
            </p>
          </Panel>
        </>
      ) : state === 'suspended' ? (
        <Panel
          title="Locked while this account is suspended"
          description="The QuickBooks connection and user management are locked until iERP reinstates the account."
        >
          <p className="panel-footnote">
            Contact iERP to have it reinstated. Everything already posted stays exactly as it is.
          </p>
        </Panel>
      ) : null}
      <ChangePasswordPanel />
    </>
  );
}
